import { useEffect, useState } from "react";

import api from "../api/client";

type Customer = { id: number; name: string };
type Opportunity = {
  id: number;
  customer_id: number | null;
  prospect_name: string | null;
  title: string;
  stage: string;
  expected_value_cents: number;
  probability_pct: number;
  weighted_cents: number;
  expected_close_date: string | null;
  actual_close_date: string | null;
  owner_user_id: number | null;
  source: string | null;
  notes: string | null;
  is_archived: boolean;
};

const STAGES = ["lead", "qualified", "proposal", "won", "lost"] as const;
type Stage = (typeof STAGES)[number];

const STAGE_COLOR: Record<Stage, string> = {
  lead: "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600",
  qualified: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700",
  proposal: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700",
  won: "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700",
  lost: "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700",
};

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const blank = (): Partial<Opportunity> => ({
  prospect_name: "",
  title: "",
  stage: "lead",
  expected_value_cents: 0,
  probability_pct: 10,
});

export default function PSAPipeline() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [byStage, setByStage] = useState<Record<Stage, Opportunity[]>>({
    lead: [],
    qualified: [],
    proposal: [],
    won: [],
    lost: [],
  });
  const [summary, setSummary] = useState<Record<string, { count: number; total_cents: number; weighted_cents: number }>>({});
  const [editing, setEditing] = useState<Partial<Opportunity> | null>(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [movingId, setMovingId] = useState<number | null>(null);

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data || []));
  }, []);

  const load = async () => {
    setErr("");
    try {
      const [opps, summ] = await Promise.all([
        api.get("/api/v1/psa/opportunities", { params: { archived: false } }),
        api.get("/api/v1/psa/pipeline/summary"),
      ]);
      const grouped: Record<Stage, Opportunity[]> = {
        lead: [],
        qualified: [],
        proposal: [],
        won: [],
        lost: [],
      };
      for (const o of opps.data as Opportunity[]) {
        if ((STAGES as readonly string[]).includes(o.stage)) {
          grouped[o.stage as Stage].push(o);
        }
      }
      setByStage(grouped);
      setSummary(summ.data || {});
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load pipeline");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setErr("");
    try {
      if (editing.id) {
        await api.patch(`/api/v1/psa/opportunities/${editing.id}`, editing);
      } else {
        await api.post("/api/v1/psa/opportunities", editing);
      }
      setEditing(null);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const moveStage = async (opp: Opportunity, stage: Stage) => {
    setMovingId(opp.id);
    try {
      await api.post(`/api/v1/psa/opportunities/${opp.id}/stage`, { stage });
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Stage transition failed");
    } finally {
      setMovingId(null);
    }
  };

  const cName = (id: number | null, prospect: string | null) =>
    id !== null
      ? customers.find((c) => c.id === id)?.name || `customer ${id}`
      : prospect || "—";

  const weightedTotal = Object.values(summary).reduce(
    (sum, s) => sum + (s?.weighted_cents || 0),
    0,
  );
  const openTotal = ["lead", "qualified", "proposal"].reduce(
    (sum, k) => sum + (summary[k]?.total_cents || 0),
    0,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            PSA — Sales pipeline
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Lead → Qualified → Proposal → Won / Lost. Light CRM for the next
            ~12 months of MSP pipeline.
          </p>
        </div>
        <button
          onClick={() => setEditing(blank())}
          className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90"
        >
          New opportunity
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat label="Open pipeline (Lead + Qualified + Proposal)" value={fmt(openTotal)} />
        <Stat label="Weighted (all stages)" value={fmt(weightedTotal)} />
        <Stat
          label="Won (lifetime, open opps)"
          value={fmt(summary.won?.total_cents || 0)}
        />
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{err}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {STAGES.map((stage) => (
          <div
            key={stage}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 min-h-[300px]"
          >
            <div className="flex items-baseline justify-between mb-2">
              <div className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STAGE_COLOR[stage]}`}>
                {stage.toUpperCase()}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {summary[stage]?.count ?? 0}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 font-mono">
              {fmt(summary[stage]?.total_cents ?? 0)}
              {summary[stage] && stage !== "won" && stage !== "lost" && (
                <span className="text-[10px] ml-1 text-gray-400 dark:text-gray-500">
                  · weighted {fmt(summary[stage].weighted_cents)}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {byStage[stage].map((opp) => (
                <div
                  key={opp.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-xs"
                >
                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-0.5">
                    {opp.title}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 mb-1">
                    {cName(opp.customer_id, opp.prospect_name)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                      {fmt(opp.expected_value_cents)}{" "}
                      <span className="text-gray-400 dark:text-gray-500">@ {opp.probability_pct}%</span>
                    </span>
                    <button
                      onClick={() => setEditing(opp)}
                      className="text-brand dark:text-brand-light hover:underline text-[10px]"
                    >
                      Edit
                    </button>
                  </div>
                  {opp.expected_close_date && (
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                      Close: {opp.expected_close_date}
                    </div>
                  )}
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {STAGES.filter((s) => s !== stage).map((s) => (
                      <button
                        key={s}
                        onClick={() => moveStage(opp, s)}
                        disabled={movingId === opp.id}
                        className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STAGE_COLOR[s]} hover:opacity-80 disabled:opacity-50`}
                      >
                        → {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {byStage[stage].length === 0 && (
                <div className="text-[11px] text-gray-400 dark:text-gray-500 italic text-center py-3">empty</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
              {editing.id ? "Edit opportunity" : "New opportunity"}
            </h2>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Title</span>
                <input
                  value={editing.title ?? ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Existing customer</span>
                  <select
                    value={editing.customer_id ?? ""}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        customer_id: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  >
                    <option value="">— Prospect, not yet customer —</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Prospect name (if no customer)</span>
                  <input
                    value={editing.prospect_name ?? ""}
                    onChange={(e) => setEditing({ ...editing, prospect_name: e.target.value })}
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Expected value ($)</span>
                  <input
                    type="number"
                    value={(editing.expected_value_cents ?? 0) / 100}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        expected_value_cents: Math.round(Number(e.target.value) * 100),
                      })
                    }
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Probability %</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={editing.probability_pct ?? 10}
                    onChange={(e) =>
                      setEditing({ ...editing, probability_pct: Number(e.target.value) })
                    }
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Expected close</span>
                  <input
                    type="date"
                    value={editing.expected_close_date ?? ""}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        expected_close_date: e.target.value || null,
                      })
                    }
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
              </div>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Source</span>
                <input
                  value={editing.source ?? ""}
                  onChange={(e) => setEditing({ ...editing, source: e.target.value })}
                  placeholder="referral / renewal / marketing / …"
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Notes</span>
                <textarea
                  value={editing.notes ?? ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  rows={3}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border dark:border-gray-600 dark:text-gray-200 rounded-lg">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || !editing.title}
                className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1 font-mono">{value}</div>
    </div>
  );
}
